import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, Users, FileText, ArrowLeft, Download, RefreshCw, Building2, UserPlus, Trash2, X, Crown, ArrowDown, Activity, Clock, CheckCircle, FileEdit, Upload, AlertTriangle, CreditCard, Package, Settings, Edit3, Save } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../lib/apiUrl';
import { PLAN_PRICING, USER_ADD_ONS } from '../lib/pricing';

// Role hierarchy for determining transfer targets
const ROLE_HIERARCHY = {
  'super_admin': 5,
  'admin': 4,
  'security_officer': 3,
  'compliance_analyst': 2,
  'user': 1
};

// Format date and time from ISO string - simple display without timezone conversion
const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  try {
    // Extract date and time directly from ISO string
    const dateTimePart = isoString.replace('T', ' ').split('.')[0].split('+')[0];
    return dateTimePart; // Returns "YYYY-MM-DD HH:MM:SS"
  } catch {
    return isoString;
  }
};

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const requestedFilter = searchParams.get('filter');
  const [activeTab, setActiveTab] = useState(requestedTab === 'assessments' ? 'assessments' : 'activity');
  const [assessmentFilter, setAssessmentFilter] = useState(['all', 'draft', 'in_progress', 'completed'].includes(requestedFilter) ? requestedFilter : 'all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', name: '', role: 'user' });
  const [inviting, setInviting] = useState(false);
  const [seatStatus, setSeatStatus] = useState(null);
  const [invitationResult, setInvitationResult] = useState(null);
  const [showStepDownModal, setShowStepDownModal] = useState(false);
  const [stepDownRole, setStepDownRole] = useState('admin');
  const [steppingDown, setSteppingDown] = useState(false);
  const [promotingUser, setPromotingUser] = useState(null);
  // Selection states for bulk delete
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedAssessments, setSelectedAssessments] = useState([]);
  const [deleting, setDeleting] = useState(false);
  // Import users state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importedUsers, setImportedUsers] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  // Delete user with transfer state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [transferToUser, setTransferToUser] = useState('');
  const [deletingUser, setDeletingUser] = useState(false);
  // Custom role display names
  const [customRoleNames, setCustomRoleNames] = useState(() => {
    const saved = localStorage.getItem('customRoleNames');
    return saved ? JSON.parse(saved) : {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'org_admin': 'Org Admin',
      'security_officer': 'Security Officer',
      'compliance_analyst': 'Compliance Analyst',
      'user': 'User'
    };
  });
  const [editingRoles, setEditingRoles] = useState(false);
  const [tempRoleNames, setTempRoleNames] = useState({});
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [usersRes, assessmentsRes, leadsRes, statsRes, seatRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/assessments`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/subscription/check-limit/user`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      setUsers(usersRes.data);
      setAssessments(assessmentsRes.data.assessments || []);
      setDrafts(assessmentsRes.data.drafts || []);
      setActivityLogs(leadsRes.data.leads || []);
      setStats(statsRes.data);
      setSeatStatus(seatRes.data);
      if (isRefresh) toast.success('Data refreshed');
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (searchParams.get('tab') === 'assessments') setActiveTab('assessments');
    const filter = searchParams.get('filter');
    if (['all', 'draft', 'in_progress', 'completed'].includes(filter)) setAssessmentFilter(filter);
  }, [searchParams]);

  const handleAssessmentFilterChange = (value) => {
    setAssessmentFilter(value);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', 'assessments');
    if (value === 'all') nextParams.delete('filter');
    else nextParams.set('filter', value);
    setSearchParams(nextParams, { replace: true });
  };

  const handleContinueAssessment = (assessment) => {
    navigate(assessment.resume_url || `/organization?orgId=${assessment.org_id}`);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/export-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { csv_content, filename } = response.data;
      
      // Create and download file
      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success(`Exported ${response.data.total_users} users to CSV`);
    } catch (error) {
      toast.error('Failed to export users');
    } finally {
      setExporting(false);
    }
  };

  const handleExportActivityCSV = () => {
    if (activityLogs.length === 0) {
      toast.error('No activity logs to export');
      return;
    }

    // Create CSV content with all activity log data
    const headers = ['Name', 'Email', 'Role', 'Organization', 'Website', 'Industry', 'Employees', 'Registered Date & Time'];
    const rows = activityLogs.map(log => [
      log.name || '',
      log.email || '',
      log.role || 'user',
      log.organization || '',
      log.website || '',
      log.industry || '',
      log.employees || '',
      formatDateTime(log.registered_at)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${activityLogs.length} activity logs to CSV`);
  };

  const handleInviteTeamMember = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/team/invite`, inviteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitationResult(response.data);
      if (response.data.email_sent) toast.success(`Invitation email sent to ${inviteData.email}.`);
      else toast.warning('Invitation created, but email delivery is unavailable. Copy the joining link below.');
      setInviteData({ email: '', name: '', role: 'user' });
      fetchData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite team member');
    } finally {
      setInviting(false);
    }
  };

  const openInviteModal = () => {
    setInvitationResult(null);
    setShowInviteModal(true);
  };

  const handleGrantUserSeat = async (targetUser) => {
    if (!window.confirm(`Confirm payment and add one user seat for ${targetUser.email}?`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/subscription/grant-user-seat`, {
        user_id: targetUser.user_id,
        seats: 1,
        payment_reference: 'Manual confirmation by Super Admin'
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Added one user seat for ${targetUser.email}.`);
      fetchData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user seat');
    }
  };

  const handleCopyInvitationLink = async () => {
    const link = invitationResult?.joining_link || '';
    if (!link) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      toast.success('Joining link copied.');
    } catch (error) {
      toast.warning('Copy is blocked by the browser. Select and copy the joining link manually.');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/admin/team/${userId}/role`, 
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Role updated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (userObj) => {
    setUserToDelete(userObj);
    // Set default transfer target to highest authority user (excluding the user to delete and current user)
    const eligibleUsers = users
      .filter(u => u.user_id !== userObj.user_id && u.user_id !== user?.user_id)
      .sort((a, b) => (ROLE_HIERARCHY[b.role] || 0) - (ROLE_HIERARCHY[a.role] || 0));
    
    if (eligibleUsers.length > 0) {
      setTransferToUser(eligibleUsers[0].user_id);
    } else {
      setTransferToUser('');
    }
    setShowDeleteModal(true);
  };

  // Handle delete with data transfer
  const handleDeleteWithTransfer = async () => {
    if (!userToDelete) return;
    
    setDeletingUser(true);
    try {
      // Call API to transfer data and delete user
      const url = transferToUser 
        ? `${API_URL}/api/admin/team/${userToDelete.user_id}?transfer_to_user_id=${transferToUser}`
        : `${API_URL}/api/admin/team/${userToDelete.user_id}`;
      
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`User ${userToDelete.email} removed successfully${transferToUser ? ' and data transferred' : ''}`);
      setShowDeleteModal(false);
      setUserToDelete(null);
      setTransferToUser('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    } finally {
      setDeletingUser(false);
    }
  };

  // Get eligible users for data transfer (sorted by role hierarchy)
  const getTransferTargets = () => {
    if (!userToDelete) return [];
    return users
      .filter(u => u.user_id !== userToDelete.user_id && u.user_id !== user?.user_id)
      .sort((a, b) => (ROLE_HIERARCHY[b.role] || 0) - (ROLE_HIERARCHY[a.role] || 0));
  };

  // Download sample Excel template
  const downloadSampleExcel = () => {
    const sampleData = [
      { 'Name': 'John Doe', 'Email ID': 'john.doe@company.com', 'Role': 'security_officer' },
      { 'Name': 'Jane Smith', 'Email ID': 'jane.smith@company.com', 'Role': 'compliance_analyst' },
      { 'Name': 'Bob Wilson', 'Email ID': 'bob.wilson@company.com', 'Role': 'user' }
    ];
    
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 20 }, // Name
      { wch: 30 }, // Email ID
      { wch: 20 }  // Role
    ];
    
    XLSX.writeFile(workbook, 'AIVORA_Comply360_User_Import_Template.xlsx');
    toast.success('Sample template downloaded');
  };

  // Handle file selection for import
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload an Excel file (.xlsx, .xls) or CSV file');
      return;
    }
    
    setImportFile(file);
    
    // Parse the file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Validate and normalize data
        const validRoles = ['admin', 'security_officer', 'compliance_analyst', 'user'];
        const normalizedData = jsonData.map((row, index) => {
          const name = row['Name'] || row['name'] || '';
          const email = row['Email ID'] || row['Email'] || row['email'] || row['email_id'] || '';
          let role = (row['Role'] || row['role'] || 'user').toLowerCase().replace(/\s+/g, '_');
          
          // Validate role
          if (!validRoles.includes(role)) {
            role = 'user';
          }
          
          return {
            name: name.trim(),
            email: email.trim().toLowerCase(),
            role,
            valid: name.trim() && email.trim() && email.includes('@'),
            rowNumber: index + 2 // +2 because Excel is 1-indexed and has header
          };
        });
        
        setImportedUsers(normalizedData);
        
        const invalidCount = normalizedData.filter(u => !u.valid).length;
        if (invalidCount > 0) {
          toast.warning(`${invalidCount} row(s) have invalid data and will be skipped`);
        }
      } catch (error) {
        toast.error('Failed to parse file. Please check the format.');
        setImportFile(null);
        setImportedUsers([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Handle bulk import
  const handleBulkImport = async () => {
    const validUsers = importedUsers.filter(u => u.valid);
    
    if (validUsers.length === 0) {
      toast.error('No valid users to import');
      return;
    }
    
    setImporting(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/team/bulk-import`, 
        { users: validUsers.map(u => ({ name: u.name, email: u.email, role: u.role })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const { imported, skipped, errors } = response.data;
      
      if (imported > 0) {
        toast.success(`Successfully imported ${imported} user(s)`);
      }
      if (skipped > 0) {
        toast.info(`${skipped} user(s) already exist and were skipped`);
      }
      if (errors && errors.length > 0) {
        toast.error(`${errors.length} user(s) failed to import`);
      }
      
      setShowImportModal(false);
      setImportFile(null);
      setImportedUsers([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import users');
    } finally {
      setImporting(false);
    }
  };

  const handleRemoveTeamMember = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from your organization?`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/admin/team/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Team member removed successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  // Promote user to Super Admin
  const handlePromoteToSuperAdmin = async (targetUser) => {
    if (!window.confirm(`Are you sure you want to promote ${targetUser.name} (${targetUser.email}) to Super Admin? They will have full system access.`)) {
      return;
    }
    setPromotingUser(targetUser.user_id);
    try {
      await axios.post(`${API_URL}/api/admin/promote-super-admin`, 
        { user_id: targetUser.user_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${targetUser.name} has been promoted to Super Admin`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote user');
    } finally {
      setPromotingUser(null);
    }
  };

  // Step down from Super Admin role
  const handleStepDown = async () => {
    setSteppingDown(true);
    try {
      await axios.post(`${API_URL}/api/admin/step-down`, 
        { new_role: stepDownRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`You have stepped down to ${getRoleDisplayName(stepDownRole)}. Please log in again.`);
      setShowStepDownModal(false);
      // Log out the user so they can log back in with new role
      setTimeout(() => {
        logout();
        navigate('/auth');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to step down');
    } finally {
      setSteppingDown(false);
    }
  };

  // Bulk selection handlers
  const handleSelectAllActivities = (checked) => {
    if (checked) {
      setSelectedActivities(activityLogs.filter(log => log.user_id !== user?.user_id).map(log => log.user_id));
    } else {
      setSelectedActivities([]);
    }
  };

  const handleSelectActivity = (userId, checked) => {
    if (checked) {
      setSelectedActivities(prev => [...prev, userId]);
    } else {
      setSelectedActivities(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAllUsers = (checked) => {
    if (checked) {
      setSelectedUsers(users.filter(u => u.user_id !== user?.user_id).map(u => u.user_id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAllAssessments = (checked) => {
    if (checked) {
      setSelectedAssessments(assessments.map(a => a.org_id));
    } else {
      setSelectedAssessments([]);
    }
  };

  const handleSelectAssessment = (orgId, checked) => {
    if (checked) {
      setSelectedAssessments(prev => [...prev, orgId]);
    } else {
      setSelectedAssessments(prev => prev.filter(id => id !== orgId));
    }
  };

  // Bulk delete handlers
  const handleBulkDeleteActivities = async () => {
    if (selectedActivities.length === 0) {
      toast.error('Please select at least one record to delete');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedActivities.length} selected user(s)? This action cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await Promise.all(
        selectedActivities.map(userId => 
          axios.delete(`${API_URL}/api/admin/team/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(`Successfully deleted ${selectedActivities.length} user(s)`);
      setSelectedActivities([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete selected users');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user to delete');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} selected user(s)? This action cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await Promise.all(
        selectedUsers.map(userId => 
          axios.delete(`${API_URL}/api/admin/team/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(`Successfully deleted ${selectedUsers.length} user(s)`);
      setSelectedUsers([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete selected users');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDeleteAssessments = async () => {
    if (selectedAssessments.length === 0) {
      toast.error('Please select at least one assessment to delete');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedAssessments.length} selected assessment(s)? This will delete all related data and cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await Promise.all(
        selectedAssessments.map(orgId => 
          axios.delete(`${API_URL}/api/admin/assessment/${orgId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(`Successfully deleted ${selectedAssessments.length} assessment(s)`);
      setSelectedAssessments([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete selected assessments');
    } finally {
      setDeleting(false);
    }
  };

  // Delete single assessment
  const handleDeleteSingleAssessment = async (orgId, orgName) => {
    if (!window.confirm(`Are you sure you want to delete the assessment for "${orgName}"? This will delete all related data and cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/admin/assessment/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Assessment for "${orgName}" has been deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete assessment');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-700';
      case 'admin':
      case 'org_admin':
        return 'bg-blue-100 text-blue-700';
      case 'security_officer':
        return 'bg-green-100 text-green-700';
      case 'compliance_analyst':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getRoleDisplayName = (role) => {
    return customRoleNames[role] || role;
  };

  // Save custom role names
  const saveCustomRoleNames = () => {
    setCustomRoleNames(tempRoleNames);
    localStorage.setItem('customRoleNames', JSON.stringify(tempRoleNames));
    setEditingRoles(false);
    toast.success('Role display names saved');
  };

  // Start editing role names
  const startEditingRoles = () => {
    setTempRoleNames({ ...customRoleNames });
    setEditingRoles(true);
  };

  // Cancel editing
  const cancelEditingRoles = () => {
    setTempRoleNames({});
    setEditingRoles(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isSuperAdmin = user?.role === 'super_admin';

  return (
    <div className="min-h-screen overflow-x-hidden bg-slate-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center space-x-2 sm:space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                data-testid="back-to-dashboard-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-3">
                <Shield className="h-8 w-8 text-slate-900" />
                <div>
                  <h1 className="font-heading text-xl font-bold text-slate-900 sm:text-2xl">
                    {isSuperAdmin ? 'Super Admin Panel' : 'Organization Admin'}
                  </h1>
                  {!isSuperAdmin && (
                    <p className="text-sm text-slate-500">Managing your organization&apos;s team</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex w-full items-center gap-3 sm:w-auto">
              {user?.role === 'super_admin' && (
                <Button
                  variant="outline"
                  onClick={() => setShowStepDownModal(true)}
                  className="flex-1 border-orange-200 text-orange-700 hover:bg-orange-50 sm:flex-none"
                  data-testid="admin-step-down-button"
                >
                  <ArrowDown className="h-4 w-4 mr-2" />
                  Step Down
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handleRefresh}
                className="flex-1 sm:flex-none"
                data-testid="refresh-button"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Stats */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-lg border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="font-heading font-bold text-3xl text-slate-900 mb-1">{stats?.total_users || 0}</div>
              <div className="font-mono text-xs uppercase tracking-wide text-slate-500">
                {isSuperAdmin ? 'Total Users' : 'Team Members'}
              </div>
            </div>

            {isSuperAdmin && (
              <div className="bg-white p-6 rounded-lg shadow-lg border border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <Building2 className="h-8 w-8 text-purple-600" />
                </div>
                <div className="font-heading font-bold text-3xl text-slate-900 mb-1">{stats?.total_organizations || 0}</div>
                <div className="font-mono text-xs uppercase tracking-wide text-slate-500">Organizations</div>
              </div>
            )}

            {/* Invite User Button Card */}
            <div 
              className="bg-white p-6 rounded-lg shadow-lg border border-slate-200 hover:border-green-300 hover:shadow-xl transition-all cursor-pointer"
              onClick={openInviteModal}
              data-testid="admin-invite-user-card"
            >
              <div className="flex items-center justify-between mb-4">
                <UserPlus className="h-8 w-8 text-green-600" />
              </div>
              <div className="font-heading font-bold text-xl text-slate-900 mb-1">Invite User</div>
                <div className="font-mono text-xs uppercase tracking-wide text-slate-500" data-testid="admin-seat-status">{seatStatus ? `${seatStatus.current + seatStatus.pending} of ${seatStatus.limit >= 999999 ? '∞' : seatStatus.limit} seats reserved` : 'Add Team Member'}</div>
            </div>

            {/* Import Users Button Card */}
            <div 
              className="bg-white p-6 rounded-lg shadow-lg border border-slate-200 hover:border-orange-300 hover:shadow-xl transition-all cursor-pointer"
              onClick={() => setShowImportModal(true)}
            >
              <div className="flex items-center justify-between mb-4">
                <Upload className="h-8 w-8 text-orange-600" />
              </div>
              <div className="font-heading font-bold text-xl text-slate-900 mb-1">Import Users</div>
              <div className="font-mono text-xs uppercase tracking-wide text-slate-500">Bulk Add Users</div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow-lg border border-slate-200 mb-8">
            <div className="overflow-x-auto border-b border-slate-200">
              <div className="flex min-w-max">
                <button
                  onClick={() => setActiveTab('activity')}
                  className={`px-6 py-4 font-mono text-sm uppercase tracking-wide border-b-2 transition-colors ${
                    activeTab === 'activity'
                      ? 'border-slate-900 text-slate-900'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Activity className="h-4 w-4 inline mr-2" />
                  Activities Log
                </button>
                <button
                  onClick={() => setActiveTab('users')}
                  className={`px-6 py-4 font-mono text-sm uppercase tracking-wide border-b-2 transition-colors ${
                    activeTab === 'users'
                      ? 'border-slate-900 text-slate-900'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Users className="h-4 w-4 inline mr-2" />
                  Roles & Permissions
                  <span className="ml-2 bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">{users.length}</span>
                </button>
                <button
                  onClick={() => setActiveTab('assessments')}
                  className={`px-6 py-4 font-mono text-sm uppercase tracking-wide border-b-2 transition-colors ${
                    activeTab === 'assessments'
                      ? 'border-slate-900 text-slate-900'
                      : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <FileText className="h-4 w-4 inline mr-2" />
                  Assessments
                </button>
                {user?.role === 'super_admin' && (
                  <button
                    onClick={() => setActiveTab('subscriptions')}
                    className={`px-6 py-4 font-mono text-sm uppercase tracking-wide border-b-2 transition-colors ${
                      activeTab === 'subscriptions'
                        ? 'border-slate-900 text-slate-900'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <CreditCard className="h-4 w-4 inline mr-2" />
                    Subscriptions
                  </button>
                )}
                {user?.role === 'super_admin' && (
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`px-6 py-4 font-mono text-sm uppercase tracking-wide border-b-2 transition-colors ${
                      activeTab === 'settings'
                        ? 'border-slate-900 text-slate-900'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <Settings className="h-4 w-4 inline mr-2" />
                    Settings
                  </button>
                )}
              </div>
            </div>

            {/* Activities Log Tab */}
            {activeTab === 'activity' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading font-semibold text-2xl text-slate-900">Activities Log</h2>
                    <p className="text-slate-500 text-sm mt-1">
                      Real-time usage data and user activities
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {selectedActivities.length > 0 && (
                      <Button 
                        variant="destructive" 
                        onClick={handleBulkDeleteActivities}
                        disabled={deleting}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Selected ({selectedActivities.length})
                      </Button>
                    )}
                    <Button onClick={handleExportActivityCSV} disabled={activityLogs.length === 0}>
                      <Download className="h-4 w-4 mr-2" />
                      Export to CSV
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="py-3 px-4 w-12">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            checked={selectedActivities.length === activityLogs.filter(l => l.user_id !== user?.user_id).length && activityLogs.length > 0}
                            onChange={(e) => handleSelectAllActivities(e.target.checked)}
                          />
                        </th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">User</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Email</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Role</th>
                        {isSuperAdmin && (
                          <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Organization</th>
                        )}
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Registered</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityLogs.length === 0 ? (
                        <tr>
                          <td colSpan={isSuperAdmin ? 7 : 6} className="py-8 text-center text-slate-500">
                            No activity logs yet.
                          </td>
                        </tr>
                      ) : (
                        activityLogs.map((log, index) => (
                          <tr key={index} className={`border-b border-slate-100 hover:bg-slate-50 ${selectedActivities.includes(log.user_id) ? 'bg-blue-50' : ''}`}>
                            <td className="py-3 px-4">
                              {log.user_id !== user?.user_id && (
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                  checked={selectedActivities.includes(log.user_id)}
                                  onChange={(e) => handleSelectActivity(log.user_id, e.target.checked)}
                                />
                              )}
                            </td>
                            <td className="py-3 px-4 font-body text-sm text-slate-900">{log.name || '-'}</td>
                            <td className="py-3 px-4 font-body text-sm text-slate-700">{log.email}</td>
                            <td className="py-3 px-4">
                              <span className={`font-mono text-xs uppercase px-2 py-1 rounded ${getRoleBadgeColor(log.role)}`}>
                                {log.role?.replace('_', ' ') || 'user'}
                              </span>
                            </td>
                            {isSuperAdmin && (
                              <td className="py-3 px-4 font-body text-sm text-slate-700">
                                {log.organization || <span className="text-slate-400">-</span>}
                              </td>
                            )}
                            <td className="py-3 px-4 font-body text-sm text-slate-500">
                              {formatDateTime(log.registered_at)}
                            </td>
                            <td className="py-3 px-4">
                              {log.user_id !== user?.user_id && user?.role === 'super_admin' && log.role !== 'super_admin' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handlePromoteToSuperAdmin(log)}
                                  disabled={promotingUser === log.user_id}
                                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                  title="Promote to Super Admin"
                                >
                                  <Crown className="h-4 w-4" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Roles & Permissions Tab */}
            {activeTab === 'users' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading font-semibold text-2xl text-slate-900">Roles & Permissions</h2>
                    <p className="text-slate-500 text-sm mt-1">
                      {users.length} {users.length === 1 ? 'user' : 'users'} registered
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {selectedUsers.length > 0 && (
                      <Button 
                        variant="destructive" 
                        onClick={handleBulkDeleteUsers}
                        disabled={deleting}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Selected ({selectedUsers.length})
                      </Button>
                    )}
                    <Button onClick={handleExportCSV} disabled={exporting}>
                      <Download className={`h-4 w-4 mr-2 ${exporting ? 'animate-pulse' : ''}`} />
                      {exporting ? 'Exporting...' : 'Export to CSV'}
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="py-3 px-4 w-12">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            checked={selectedUsers.length === users.filter(u => u.user_id !== user?.user_id).length && users.length > 1}
                            onChange={(e) => handleSelectAllUsers(e.target.checked)}
                          />
                        </th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Name</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Email</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Role</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Created Date & Time</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-slate-500">
                            No users registered yet.
                          </td>
                        </tr>
                      ) : (
                        users.map((u) => (
                          <tr key={u.user_id} className={`border-b border-slate-100 hover:bg-slate-50 ${selectedUsers.includes(u.user_id) ? 'bg-blue-50' : ''}`}>
                            <td className="py-3 px-4">
                              {u.user_id !== user?.user_id && (
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                  checked={selectedUsers.includes(u.user_id)}
                                  onChange={(e) => handleSelectUser(u.user_id, e.target.checked)}
                                />
                              )}
                            </td>
                            <td className="py-3 px-4 font-body text-sm text-slate-900">{u.name || '-'}</td>
                            <td className="py-3 px-4 font-body text-sm text-slate-700">{u.email}</td>
                            <td className="py-3 px-4">
                              {u.user_id === user?.user_id ? (
                                <span className={`font-mono text-xs uppercase px-2 py-1 rounded ${getRoleBadgeColor(u.role)}`}>
                                  {getRoleDisplayName(u.role)} (You)
                                </span>
                              ) : (
                                <Select
                                  value={u.role}
                                  onValueChange={(value) => handleUpdateRole(u.user_id, value)}
                                >
                                  <SelectTrigger className="w-44">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="security_officer">Security Officer</SelectItem>
                                    <SelectItem value="compliance_analyst">Compliance Analyst</SelectItem>
                                    <SelectItem value="user">User</SelectItem>
                                    {isSuperAdmin && (
                                      <SelectItem value="super_admin">Super Admin</SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="py-3 px-4 font-body text-sm text-slate-500">
                              {formatDateTime(u.created_at)}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-1">
                                {u.user_id !== user?.user_id && user?.role === 'super_admin' && u.role !== 'super_admin' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handlePromoteToSuperAdmin(u)}
                                    disabled={promotingUser === u.user_id}
                                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                    title="Promote to Super Admin"
                                  >
                                    <Crown className="h-4 w-4" />
                                  </Button>
                                )}
                                {u.user_id !== user?.user_id && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openDeleteModal(u)}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Delete User"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Assessments Tab */}
            {activeTab === 'assessments' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading font-semibold text-2xl text-slate-900">
                      {isSuperAdmin ? 'All Assessments' : 'Team Assessments'}
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">Track assessment progress across all stages</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {selectedAssessments.length > 0 && (
                      <Button 
                        variant="destructive" 
                        onClick={handleBulkDeleteAssessments}
                        disabled={deleting}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Selected ({selectedAssessments.length})
                      </Button>
                    )}
                    <Select value={assessmentFilter} onValueChange={handleAssessmentFilterChange}>
                      <SelectTrigger className="w-40" data-testid="assessment-status-filter">
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Status Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-slate-500" />
                      <span className="text-sm text-slate-500">Total</span>
                    </div>
                    <span className="text-2xl font-bold text-slate-900" data-testid="admin-total-assessments-count">{assessments.length + drafts.filter((draft) => !draft.org_id).length}</span>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <div className="flex items-center gap-2 mb-1">
                      <FileEdit className="h-4 w-4 text-amber-600" />
                      <span className="text-sm text-amber-600">Draft</span>
                    </div>
                    <span className="text-2xl font-bold text-amber-700" data-testid="admin-unbound-drafts-count">{drafts.filter((draft) => !draft.org_id).length}</span>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-blue-600">In Progress</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-700" data-testid="admin-in-progress-assessments-count">
                      {assessments.filter(a => a.status === 'in_progress').length + drafts.filter((draft) => !draft.org_id).length}
                    </span>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-green-600">Completed</span>
                    </div>
                    <span className="text-2xl font-bold text-green-700">
                      {assessments.filter(a => a.status === 'completed').length}
                    </span>
                  </div>
                </div>

                {/* Drafts Section */}
                {(assessmentFilter === 'all' || assessmentFilter === 'draft' || assessmentFilter === 'in_progress') && drafts.filter((draft) => !draft.org_id).length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-lg text-slate-800 mb-3 flex items-center gap-2">
                      <FileEdit className="h-5 w-5 text-amber-600" />
                      Draft Assessments
                    </h3>
                    <div className="overflow-x-auto border border-amber-200 rounded-lg">
                      <table className="w-full">
                        <thead className="bg-amber-50">
                          <tr className="border-b border-amber-200">
                            <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-amber-700">Stage</th>
                            <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-amber-700">User</th>
                            <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-amber-700">Email</th>
                            <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-amber-700">Last Updated</th>
                            <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-amber-700">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drafts.filter((draft) => !draft.org_id).map((draft, index) => (
                            <tr key={draft.draft_id || index} className="border-b border-amber-100 hover:bg-amber-50/50" data-testid={`unbound-draft-row-${draft.draft_id || index}`}>
                              <td className="py-3 px-4 font-body text-sm text-slate-900">{draft.draft_type_name}</td>
                              <td className="py-3 px-4 font-body text-sm text-slate-700">{draft.user_name}</td>
                              <td className="py-3 px-4 font-body text-sm text-slate-600">{draft.user_email}</td>
                              <td className="py-3 px-4 font-body text-sm text-slate-500">{formatDateTime(draft.updated_at)}</td>
                              <td className="py-3 px-4"><Button size="sm" variant="outline" onClick={() => navigate(draft.resume_url)} data-testid={`continue-draft-${draft.draft_id || index}`}>Continue</Button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* In Progress & Completed Assessments */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="py-3 px-4 w-12">
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            checked={selectedAssessments.length === assessments.filter(a => assessmentFilter === 'all' || a.status === assessmentFilter).length && assessments.length > 0}
                            onChange={(e) => handleSelectAllAssessments(e.target.checked)}
                          />
                        </th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Organization</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Industry</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">User</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Status</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Current Stage</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Started</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assessments
                        .filter(a => assessmentFilter === 'all' || a.status === assessmentFilter)
                        .length === 0 ? (
                        <tr>
                          <td colSpan={8} className="py-8 text-center text-slate-500">
                            No assessments found.
                          </td>
                        </tr>
                      ) : (
                        assessments
                          .filter(a => assessmentFilter === 'all' || a.status === assessmentFilter)
                          .map((assessment, index) => (
                          <tr key={assessment.org_id || index} className={`border-b border-slate-100 hover:bg-slate-50 ${selectedAssessments.includes(assessment.org_id) ? 'bg-blue-50' : ''}`} data-testid={`admin-assessment-row-${assessment.org_id}`}>
                            <td className="py-3 px-4">
                              <input
                                type="checkbox"
                                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                checked={selectedAssessments.includes(assessment.org_id)}
                                onChange={(e) => handleSelectAssessment(assessment.org_id, e.target.checked)}
                              />
                            </td>
                            <td className="py-3 px-4 font-body text-sm text-slate-900 font-medium">
                              {assessment.org_name}
                            </td>
                            <td className="py-3 px-4 font-body text-sm text-slate-700">{assessment.industry || '-'}</td>
                            <td className="py-3 px-4">
                              <div>
                                <div className="font-body text-sm text-slate-900">{assessment.user_name}</div>
                                <div className="font-body text-xs text-slate-500">{assessment.user_email}</div>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center gap-1 font-mono text-xs uppercase px-2 py-1 rounded ${
                                assessment.status === 'completed' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {assessment.status === 'completed' ? (
                                  <CheckCircle className="h-3 w-3" />
                                ) : (
                                  <Clock className="h-3 w-3" />
                                )}
                                {assessment.status === 'completed' ? 'Completed' : 'In Progress'}
                              </span>
                            </td>
                            <td className="py-3 px-4 font-body text-sm text-slate-700">
                              <span data-testid={`assessment-current-stage-${assessment.org_id}`}>{assessment.current_stage}</span>
                            </td>
                            <td className="py-3 px-4 font-body text-sm text-slate-500">
                              {formatDateTime(assessment.created_at)}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {assessment.status === 'completed' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => navigate(`/assessment-summary/${assessment.org_id}`)}
                                  >
                                    View Report
                                  </Button>
                                )}
                                {assessment.status === 'in_progress' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleContinueAssessment(assessment)}
                                    data-testid={`continue-assessment-${assessment.org_id}`}
                                  >
                                    Continue
                                  </Button>
                                )}
                                {(user?.role === 'super_admin' || user?.role === 'org_admin') && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeleteSingleAssessment(assessment.org_id, assessment.org_name)}
                                    title="Delete Assessment"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Subscriptions Tab - Super Admin Only */}
            {activeTab === 'subscriptions' && user?.role === 'super_admin' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading font-semibold text-2xl text-slate-900">Subscription Management</h2>
                    <p className="text-slate-500 text-sm mt-1">
                      Manage user plans and subscription limits
                    </p>
                  </div>
                </div>

                {/* Plan Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
                    <div className="flex items-center gap-3 mb-3">
                      <Package className="h-6 w-6 text-blue-600" />
                      <h3 className="font-semibold text-blue-900">Standard Edition</h3>
                    </div>
                    <p className="text-3xl font-bold text-blue-700">{PLAN_PRICING.standard.amount}</p>
                    <p className="text-blue-600 text-sm">One-time • 1 User • 3 Assessments</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
                    <div className="flex items-center gap-3 mb-3">
                      <CreditCard className="h-6 w-6 text-purple-600" />
                      <h3 className="font-semibold text-purple-900">Pro Edition</h3>
                    </div>
                    <p className="text-3xl font-bold text-purple-700">{PLAN_PRICING.pro.amount}</p>
                    <p className="text-purple-600 text-sm">One-time • 3 Users • 5 Assessments (93 Controls)</p>
                  </div>
                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
                    <div className="flex items-center gap-3 mb-3">
                      <Clock className="h-6 w-6 text-slate-600" />
                      <h3 className="font-semibold text-slate-900">Additional Users</h3>
                    </div>
                    <p className="text-xl font-bold text-slate-700">{USER_ADD_ONS.standard.amount} / {USER_ADD_ONS.pro.amount}</p>
                    <p className="text-slate-600 text-sm">Standard / Pro • Per additional user</p>
                  </div>
                </div>

                {/* Users Subscription Table */}
                <div className="bg-slate-50 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-600">User</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-600">Current Plan</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-600">Status</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-600">Assessments</th>
                        <th className="text-left py-3 px-4 font-mono text-xs uppercase tracking-wide text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-b border-slate-200 hover:bg-slate-100 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-slate-600">{u.name?.[0] || u.email[0].toUpperCase()}</span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{u.name || 'Unnamed'}</p>
                                <p className="text-xs text-slate-500">{u.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              u.subscription?.plan === 'standard' ? 'bg-blue-100 text-blue-700' :
                              u.subscription?.plan === 'pro' ? 'bg-purple-100 text-purple-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {u.subscription?.plan === 'standard' ? '⭐ Standard' :
                               u.subscription?.plan === 'pro' ? '👑 Pro' :
                               '🕐 Trial'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                              u.subscription?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {u.subscription?.status === 'active' ? '✓ Active' : '✗ Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">
                            {u.subscription?.assessments_limit || '∞'} limit
                          </td>
                          <td className="py-3 px-4">
                            <Select
                              onValueChange={async (newPlan) => {
                                try {
                                  await axios.put(
                                    `${API_URL}/api/admin/subscription/update`,
                                    { user_id: u.user_id, plan: newPlan },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  toast.success(`Updated ${u.email} to ${newPlan} plan`);
                                  fetchData(true);
                                } catch (err) {
                                  toast.error('Failed to update subscription');
                                }
                              }}
                            >
                              <SelectTrigger className="w-32 h-8 text-xs">
                                <SelectValue placeholder="Change plan" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="standard">Standard</SelectItem>
                                <SelectItem value="pro">Pro</SelectItem>
                              </SelectContent>
                            </Select>
                            {['standard', 'pro'].includes(u.subscription?.plan) && (u.subscription?.users_limit || 0) < 999999 && (
                              <Button size="sm" variant="outline" onClick={() => handleGrantUserSeat(u)} className="ml-2 h-8 text-xs" data-testid={`grant-seat-${u.user_id}`}>+1 Seat</Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Settings Tab - Super Admin Only */}
            {activeTab === 'settings' && user?.role === 'super_admin' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading font-semibold text-2xl text-slate-900">Admin Settings</h2>
                    <p className="text-slate-500 text-sm mt-1">
                      Configure role display names and system preferences
                    </p>
                  </div>
                </div>

                {/* Role Display Names Configuration */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Edit3 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Role Display Names</h3>
                        <p className="text-sm text-slate-500">Customize how roles are displayed throughout the application</p>
                      </div>
                    </div>
                    {!editingRoles ? (
                      <Button onClick={startEditingRoles} variant="outline" size="sm">
                        <Edit3 className="h-4 w-4 mr-2" />
                        Edit Names
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button onClick={cancelEditingRoles} variant="outline" size="sm">
                          Cancel
                        </Button>
                        <Button onClick={saveCustomRoleNames} size="sm">
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="grid gap-4">
                      {Object.entries(editingRoles ? tempRoleNames : customRoleNames).map(([roleKey, displayName]) => (
                        <div key={roleKey} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 border border-slate-100">
                          <div className="w-40">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-mono ${getRoleBadgeColor(roleKey)}`}>
                              {roleKey}
                            </span>
                          </div>
                          <div className="flex-1 flex items-center gap-3">
                            <span className="text-slate-400">→</span>
                            {editingRoles ? (
                              <Input
                                type="text"
                                value={tempRoleNames[roleKey] || ''}
                                onChange={(e) => setTempRoleNames({ ...tempRoleNames, [roleKey]: e.target.value })}
                                className="max-w-xs text-slate-900"
                                placeholder={`Display name for ${roleKey}`}
                              />
                            ) : (
                              <span className="font-medium text-slate-700">{displayName}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="mt-4 text-xs text-slate-500">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      These changes affect display labels only. Internal role permissions remain unchanged.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Invite Team Member Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h3 className="font-heading font-semibold text-xl text-slate-900">Invite Team Member</h3>
              <button onClick={() => setShowInviteModal(false)} className="text-slate-400 hover:text-slate-600" data-testid="invite-modal-close-button">
                <X className="h-5 w-5" />
              </button>
            </div>
            {invitationResult ? (
              <div className="p-6 space-y-4" data-testid="invitation-created-result">
                <div className={`rounded-lg border p-4 text-sm ${invitationResult.email_sent ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                  {invitationResult.email_sent ? 'Invitation email delivered successfully.' : 'Email delivery is unavailable. Share this secure joining link manually.'}
                </div>
                <Input value={invitationResult.joining_link || ''} readOnly data-testid="invitation-joining-link" />
                <Button type="button" variant="outline" className="w-full" onClick={handleCopyInvitationLink} data-testid="copy-invitation-link-button">Copy Joining Link</Button>
                <Button type="button" className="w-full" onClick={() => setShowInviteModal(false)} data-testid="invitation-result-done-button">Done</Button>
              </div>
            ) : seatStatus && !seatStatus.allowed ? (
              <div className="p-6 space-y-4" data-testid="user-seat-limit-panel">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4"><p className="font-semibold text-amber-900">User capacity reached</p><p className="mt-2 text-sm text-amber-800">Your {seatStatus.plan?.toUpperCase()} workspace has reserved {seatStatus.current + seatStatus.pending} of {seatStatus.limit} seats.</p></div>
                <a href={seatStatus.payment_url || USER_ADD_ONS[seatStatus.plan]?.paymentLink} target="_blank" rel="noopener noreferrer" className="block" data-testid="purchase-user-seat-link"><Button className="w-full">Add one user for ${seatStatus.add_on_price || USER_ADD_ONS[seatStatus.plan]?.amount?.replace('$', '')}</Button></a>
                <p className="text-xs text-slate-500">After payment confirmation, a Super Admin can grant the seat from Subscriptions.</p>
              </div>
            ) : <form onSubmit={handleInviteTeamMember} className="p-6 space-y-4" data-testid="invite-team-member-form">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">
                  User Name *
                </Label>
                <Input
                  type="text"
                  value={inviteData.name}
                  onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                  placeholder="Enter full name"
                  required
                  className="text-black"
                  data-testid="invite-user-name-input"
                />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">
                  Email ID *
                </Label>
                <Input
                  type="email"
                  value={inviteData.email}
                  onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                  placeholder="user@company.com"
                  required
                  className="text-black"
                  data-testid="invite-user-email-input"
                />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">
                  Role *
                </Label>
                <Select
                  value={inviteData.role}
                  onValueChange={(value) => setInviteData({ ...inviteData, role: value })}
                >
                  <SelectTrigger className="text-black" data-testid="invite-user-role-trigger">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <div className="flex flex-col">
                        <span className="font-semibold">Admin</span>
                        <span className="text-xs text-slate-500">Full access to manage organization and team</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="security_officer">
                      <div className="flex flex-col">
                        <span className="font-semibold">Security Officer</span>
                        <span className="text-xs text-slate-500">Manage security assessments and controls</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="compliance_analyst">
                      <div className="flex flex-col">
                        <span className="font-semibold">Compliance Analyst</span>
                        <span className="text-xs text-slate-500">View and analyze assessment reports</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-2">
                  A secure joining link valid for 48 hours will be emailed to the user.
                </p>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)} data-testid="invite-user-cancel-button">
                  Cancel
                </Button>
                <Button type="submit" disabled={inviting} data-testid="invite-user-submit-button">
                  {inviting ? 'Sending Invite...' : 'Send Invitation'}
                </Button>
              </div>
            </form>}
          </motion.div>
        </div>
      )}

      {/* Step Down Modal - Super Admin Only */}
      {showStepDownModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-full">
                  <ArrowDown className="h-5 w-5 text-orange-600" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900">Step Down from Super Admin</h3>
              </div>
              <button onClick={() => setShowStepDownModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm">
                  <strong>Warning:</strong> You are about to give up your Super Admin privileges. 
                  Make sure another Super Admin exists before proceeding.
                </p>
              </div>
              
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">
                  New Role *
                </Label>
                <Select
                  value={stepDownRole}
                  onValueChange={(value) => setStepDownRole(value)}
                >
                  <SelectTrigger className="text-black">
                    <SelectValue placeholder="Select new role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <div className="flex flex-col">
                        <span className="font-semibold">Admin</span>
                        <span className="text-xs text-slate-500">Organization management access</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="security_officer">
                      <div className="flex flex-col">
                        <span className="font-semibold">Security Officer</span>
                        <span className="text-xs text-slate-500">Security assessments and team management</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="compliance_analyst">
                      <div className="flex flex-col">
                        <span className="font-semibold">Compliance Analyst</span>
                        <span className="text-xs text-slate-500">View and analyze reports</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="user">
                      <div className="flex flex-col">
                        <span className="font-semibold">User</span>
                        <span className="text-xs text-slate-500">Basic user access only</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <p className="text-sm text-slate-500">
                You will be logged out after stepping down and will need to log in again.
              </p>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowStepDownModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleStepDown} 
                  disabled={steppingDown}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {steppingDown ? 'Processing...' : 'Confirm Step Down'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Import Users Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Upload className="h-5 w-5 text-blue-600" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900">Import Users</h3>
              </div>
              <button 
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportedUsers([]);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }} 
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Download Template Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm mb-3">
                  <strong>Step 1:</strong> Download the sample Excel template, fill it with user details, and upload it below.
                </p>
                <Button variant="outline" size="sm" onClick={downloadSampleExcel}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Sample Template
                </Button>
              </div>

              {/* File Upload Section */}
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">
                  Step 2: Upload Excel File *
                </Label>
                <div className="mt-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-slate-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100
                      cursor-pointer"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Supported formats: .xlsx, .xls, .csv
                </p>
              </div>

              {/* Preview Table */}
              {importedUsers.length > 0 && (
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">
                    Preview ({importedUsers.filter(u => u.valid).length} valid, {importedUsers.filter(u => !u.valid).length} invalid)
                  </Label>
                  <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">Row</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">Name</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">Email</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">Role</th>
                          <th className="text-left py-2 px-3 text-xs font-medium text-slate-500">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importedUsers.map((u, idx) => (
                          <tr key={idx} className={`border-t border-slate-100 ${!u.valid ? 'bg-red-50' : ''}`}>
                            <td className="py-2 px-3 text-slate-500">{u.rowNumber}</td>
                            <td className="py-2 px-3 text-slate-900">{u.name || <span className="text-red-500">Missing</span>}</td>
                            <td className="py-2 px-3 text-slate-700">{u.email || <span className="text-red-500">Missing</span>}</td>
                            <td className="py-2 px-3">
                              <span className={`text-xs px-2 py-0.5 rounded ${getRoleBadgeColor(u.role)}`}>
                                {getRoleDisplayName(u.role)}
                              </span>
                            </td>
                            <td className="py-2 px-3">
                              {u.valid ? (
                                <span className="text-green-600 text-xs">✓ Valid</span>
                              ) : (
                                <span className="text-red-600 text-xs">✗ Invalid</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Role Reference */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-slate-600 text-sm font-medium mb-2">Valid Role Values:</p>
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">admin</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">security_officer</span>
                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">compliance_analyst</span>
                  <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">user</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t border-slate-200 bg-slate-50">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportedUsers([]);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleBulkImport} 
                disabled={importing || importedUsers.filter(u => u.valid).length === 0}
              >
                {importing ? 'Importing...' : `Import ${importedUsers.filter(u => u.valid).length} User(s)`}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4"
          >
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-slate-900">Delete User</h3>
              </div>
              <button 
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                  setTransferToUser('');
                }} 
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Warning Message */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm">
                  <strong>Warning:</strong> You are about to delete an active user. Please transfer the data and access to another person before proceeding.
                </p>
              </div>

              {/* User Being Deleted */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-slate-500 text-xs font-mono uppercase tracking-wide mb-2">User to be deleted</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 font-semibold text-lg">
                      {userToDelete.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{userToDelete.name}</p>
                    <p className="text-sm text-slate-500">{userToDelete.email}</p>
                  </div>
                  <span className={`ml-auto font-mono text-xs uppercase px-2 py-1 rounded ${getRoleBadgeColor(userToDelete.role)}`}>
                    {getRoleDisplayName(userToDelete.role)}
                  </span>
                </div>
              </div>

              {/* Transfer Data Section */}
              {getTransferTargets().length > 0 && (
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">
                    Transfer Data & Access To (Recommended)
                  </Label>
                  <Select
                    value={transferToUser}
                    onValueChange={(value) => setTransferToUser(value === 'none' ? '' : value)}
                  >
                    <SelectTrigger className="text-black">
                      <SelectValue placeholder="Select a user to transfer data" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-slate-500">Don&apos;t transfer data</span>
                      </SelectItem>
                      {getTransferTargets().map((u) => (
                        <SelectItem key={u.user_id} value={u.user_id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{u.name}</span>
                            <span className="text-xs text-slate-500">({getRoleDisplayName(u.role)})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-2">
                    The user&apos;s assessments, drafts, and other data will be transferred to the selected person.
                  </p>
                </div>
              )}

              {getTransferTargets().length === 0 && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-slate-600 text-sm">
                    No other users available for data transfer. The user&apos;s data will be permanently deleted.
                  </p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowDeleteModal(false);
                    setUserToDelete(null);
                    setTransferToUser('');
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleDeleteWithTransfer} 
                  disabled={deletingUser}
                >
                  {deletingUser ? 'Deleting...' : 'Delete User'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
